import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Download, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  CreditCard,
  Instagram,
  Youtube,
  CheckCircle,
  XCircle,
  Briefcase,
  Target,
  Ban,
  ShieldCheck,
  Camera,
  ClipboardList,
  Pencil,
  Filter
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { openPrivateFile } from '@/lib/privateFiles';
import { useAuth } from '@/contexts/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from '@/components/ui/input';
import { CITIES } from '@/lib/cities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(new Date(dateString));
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(dateString));
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0
  }).format(amount);
};

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  
  const [user, setUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState({});
  // Name / phone / birth date: users cannot change them once verified, admins can.
  const [editing, setEditing] = useState(null); // { field, value }
  const [savingEdit, setSavingEdit] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Activity logs state
  const [activityLogs, setActivityLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsVisible, setLogsVisible] = useState(false);
  const [logFilter, setLogFilter] = useState('all');

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;
      setUser(profileData);

      // Fetch campaign participations with campaign details
      const { data: participantData, error: participantError } = await supabase
        .from('campaign_participants')
        .select(`
          *,
          campaigns (
            id,
            title,
            status,
            start_date,
            end_date
          )
        `)
        .eq('user_id', userId)
        .order('joined_at', { ascending: false });

      if (!participantError) {
        setCampaigns(participantData || []);
      }

      // Fetch task submissions with task and campaign details
      const { data: submissionData, error: submissionError } = await supabase
        .from('task_submissions')
        .select(`
          *,
          tasks (
            id,
            title,
            platform,
            payment_amount
          ),
          campaigns (
            id,
            title
          )
        `)
        .eq('user_id', userId)
        .order('submitted_at', { ascending: false });

      if (!submissionError) {
        setSubmissions(submissionData || []);
      }

    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Kullanıcı bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async (filterType = 'all') => {
    setLogsLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) return;
      
      const url = `${BACKEND_URL}/api/admin/users/${userId}/activity-logs${filterType !== 'all' ? `?action_type=${filterType}` : ''}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActivityLogs(data);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const toggleLogs = () => {
    if (!logsVisible) {
      fetchActivityLogs(logFilter);
    }
    setLogsVisible(!logsVisible);
  };

  const handleLogFilterChange = (value) => {
    setLogFilter(value);
    fetchActivityLogs(value);
  };

  const getActionTypeLabel = (type) => {
    const labels = {
      'profile_update': 'Profil Güncelleme',
      'social_verification_success': 'Sosyal Medya Doğrulama',
      'task_submission': 'Görev Gönderimi',
      'submission_cancelled': 'Gönderim İptali',
      'campaign_join': 'Kampanya Katılım',
      'login': 'Giriş',
      'register': 'Kayıt'
    };
    return labels[type] || type;
  };

  const handleVerify = async (field, currentValue) => {
    setVerifying(prev => ({ ...prev, [field]: true }));
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const response = await fetch(
        `${BACKEND_URL}/api/admin/profiles/${userId}/verify?field=${field}&verified=${!currentValue}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Doğrulama başarısız');

      const updatedProfile = await response.json();
      setUser(updatedProfile);
      toast.success(!currentValue ? 'Doğrulandı' : 'Doğrulama kaldırıldı');
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Doğrulama işlemi başarısız');
    } finally {
      setVerifying(prev => ({ ...prev, [field]: false }));
    }
  };

  const exportToExcel = () => {
    if (!user) return;
    setExporting(true);

    try {
      const BOM = '\uFEFF';
      let csvContent = BOM;

      // User Info Section
      csvContent += 'KULLANICI BİLGİLERİ\n';
      csvContent += `E-posta;${user.email || '-'}\n`;
      csvContent += `Ad Soyad;${user.full_name || '-'}\n`;
      csvContent += `Telefon;${user.phone || '-'}\n`;
      csvContent += `IBAN;${user.iban || '-'}\n`;
      csvContent += `Doğum Tarihi;${formatDate(user.birth_date)}\n`;
      csvContent += `Şehir;${user.city || '-'}\n`;
      csvContent += `Instagram;${user.instagram_username || '-'};${user.instagram_followers || 0} takipçi\n`;
      csvContent += `TikTok;${user.tiktok_username || '-'};${user.tiktok_followers || 0} takipçi\n`;
      csvContent += `YouTube;${user.youtube_username || '-'};${user.youtube_followers || 0} abone\n`;
      csvContent += `Durum;${user.is_active ? 'Aktif' : 'Pasif'}\n`;
      csvContent += `Ban;${user.is_banned ? 'Banlı' : 'Normal'}\n`;
      csvContent += '\n';

      // Campaigns Section
      csvContent += 'KATILDIĞI KAMPANYALAR\n';
      csvContent += 'Kampanya;Katılımcı No;Katılım Tarihi;Durum\n';
      campaigns.forEach(c => {
        csvContent += `"${c.campaigns?.title || '-'}";${c.participant_number || '-'};${formatDate(c.joined_at)};${c.campaigns?.status || '-'}\n`;
      });
      csvContent += '\n';

      // Submissions Section
      csvContent += 'GÖREV GÖNDERİMLERİ\n';
      csvContent += 'Kampanya;Görev;Platform;Durum;TL;Gönderim Tarihi\n';
      submissions.forEach(s => {
        csvContent += `"${s.campaigns?.title || '-'}";"${s.tasks?.title || '-'}";${s.tasks?.platform || '-'};${s.status};${s.status === 'approved' ? formatCurrency(s.tasks?.payment_amount) : '-'};${formatDateTime(s.submitted_at)}\n`;
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const fileName = `kullanici_${user.email?.split('@')[0] || userId}_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Kullanıcı raporu indirildi');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Dışa aktarma başarısız');
    } finally {
      setExporting(false);
    }
  };

  // Calculate totals
  const approvedSubmissions = submissions.filter(s => s.status === 'approved');
  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const response = await fetch(`${BACKEND_URL}/api/admin/profiles/${userId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${sessionData?.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [editing.field]: editing.value })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Kaydedilemedi');
      setUser(prev => ({ ...prev, ...data }));
      setEditing(null);
      toast.success('Bilgi güncellendi');
    } catch (error) {
      toast.error(error.message || 'Kaydedilemedi');
    } finally {
      setSavingEdit(false);
    }
  };

  const renderEditable = (field, display, rawValue, inputType = 'text', listId) => {
    if (editing?.field === field) {
      return (
        <div className="flex items-center gap-1 w-full">
          <Input
            type={inputType}
            value={editing.value}
            onChange={(e) => setEditing({ field, value: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }}
            className="h-8"
            list={listId}
            autoFocus
            data-testid={`edit-${field}-input`}
          />
          {listId === 'city-options' && (
            <datalist id="city-options">
              {CITIES.map(city => <option key={city} value={city} />)}
            </datalist>
          )}
          <Button size="sm" className="h-8" onClick={saveEdit} disabled={savingEdit}>
            {savingEdit ? '...' : 'Kaydet'}
          </Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(null)} disabled={savingEdit}>
            İptal
          </Button>
        </div>
      );
    }
    return (
      <span className="flex items-center gap-1 min-w-0">
        <span className="font-medium truncate">{display}</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-gray-400 hover:text-gray-700"
          onClick={() => setEditing({ field, value: rawValue || '' })}
          title="Düzenle (doğrulanmışsa kullanıcı değiştiremez)"
          data-testid={`edit-${field}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </span>
    );
  };

  const VerificationBadge = ({ verified, field, label }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleVerify(field, verified)}
      disabled={verifying[field]}
      className={`h-6 px-2 ${verified ? 'text-green-600' : 'text-gray-400'}`}
    >
      {verified ? (
        <CheckCircle className="h-4 w-4 mr-1" />
      ) : (
        <XCircle className="h-4 w-4 mr-1" />
      )}
      {verifying[field] ? '...' : (verified ? 'Doğrulandı' : 'Doğrula')}
    </Button>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Kullanıcı bulunamadı</p>
        <Button onClick={() => navigate('/admin/users')} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Geri Dön
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-user-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/admin/users')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{user.full_name || user.email}</h1>
            <p className="text-gray-600 text-sm">{user.email}</p>
          </div>
          {user.is_banned && (
            <Badge variant="destructive" className="ml-2" title={user.ban_reason || undefined}>
              <Ban className="h-3 w-3 mr-1" />
              Banlı{user.ban_reason ? `: ${user.ban_reason}` : ''}
            </Badge>
          )}
          {!user.is_active && (
            <Badge variant="secondary">Pasif</Badge>
          )}
        </div>
        <Button onClick={exportToExcel} disabled={exporting}>
          <Download className="h-4 w-4 mr-2" />
          {exporting ? 'İndiriliyor...' : 'Excel İndir'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-neon rounded-lg">
                <Briefcase className="h-5 w-5 text-brand-ink" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Katıldığı Kampanya</p>
                <p className="text-2xl font-bold">{campaigns.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Target className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tamamlanan Görev</p>
                <p className="text-2xl font-bold">{approvedSubmissions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Kişisel Bilgiler
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Ad Soyad */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <User className="h-4 w-4" />
                Ad Soyad
              </div>
              <div className="flex items-center justify-between">
                {renderEditable('full_name', user.full_name || '-', user.full_name)}
                <VerificationBadge verified={user.full_name_verified} field="full_name" />
              </div>
            </div>

            {/* Telefon */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Phone className="h-4 w-4" />
                Telefon
              </div>
              <div className="flex items-center justify-between">
                {renderEditable('phone', user.phone || '-', user.phone, 'tel')}
                <VerificationBadge verified={user.phone_verified} field="phone" />
              </div>
            </div>

            {/* IBAN */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <CreditCard className="h-4 w-4" />
                IBAN
              </div>
              <div className="flex items-center justify-between">
                {renderEditable('iban', user.iban || '-', user.iban)}
                <VerificationBadge verified={user.iban_verified} field="iban" />
              </div>
            </div>

            {/* Doğum Tarihi */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                Doğum Tarihi
              </div>
              <div className="flex items-center justify-between">
                {renderEditable('birth_date', formatDate(user.birth_date), user.birth_date ? String(user.birth_date).slice(0, 10) : '', 'date')}
                <VerificationBadge verified={user.birth_date_verified} field="birth_date" />
              </div>
            </div>

            {/* Şehir */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <MapPin className="h-4 w-4" />
                Şehir
              </div>
              <div className="flex items-center justify-between">
                {renderEditable('city', user.city || '-', user.city, 'text', 'city-options')}
                <VerificationBadge verified={user.city_verified} field="city" />
              </div>
            </div>

            {/* Kayıt Tarihi */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                Kayıt Tarihi
              </div>
              <div className="flex items-center justify-between">
                <span className="font-medium">{formatDateTime(user.created_at)}</span>
              </div>
            </div>
          </div>

          <Separator className="my-6" />

          {/* Social Media */}
          <h3 className="text-lg font-semibold mb-4">Sosyal Medya Hesapları</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Instagram */}
            <div className="space-y-2 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Instagram className="h-5 w-5 text-pink-600" />
                <span className="font-medium">Instagram</span>
              </div>
              <div className="text-sm">
                <p>@{user.instagram_username || '-'}</p>
                <p className="text-gray-500">{user.instagram_followers?.toLocaleString() || 0} takipçi</p>
                {user.instagram_verified_at && (
                  <p className="text-xs text-green-700">
                    Otomatik doğrulandı: {new Date(user.instagram_verified_at).toLocaleDateString('tr-TR')}
                  </p>
                )}
                {user.instagram_checked_at && (
                  <p className="text-xs text-gray-500">
                    Takipçi son okunma: {new Date(user.instagram_checked_at).toLocaleString('tr-TR')}
                  </p>
                )}
              </div>
              <VerificationBadge verified={user.instagram_username_verified} field="instagram_username" />
              {user.instagram_screenshot_url && (
                <a 
                  href="#" onClick={(e) => { e.preventDefault(); openPrivateFile(user.instagram_screenshot_url); }} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-brand-ink font-semibold hover:underline mt-1"
                  data-testid="instagram-screenshot-link"
                >
                  <Camera className="h-3 w-3" />
                  Ekran Görüntüsü
                </a>
              )}
            </div>

            {/* TikTok */}
            <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <TikTokIcon className="h-5 w-5" />
                <span className="font-medium">TikTok</span>
              </div>
              <div className="text-sm">
                <p>@{user.tiktok_username || '-'}</p>
                <p className="text-gray-500">{user.tiktok_followers?.toLocaleString() || 0} takipçi</p>
              </div>
              <VerificationBadge verified={user.tiktok_username_verified} field="tiktok_username" />
              {user.tiktok_screenshot_url && (
                <a 
                  href="#" onClick={(e) => { e.preventDefault(); openPrivateFile(user.tiktok_screenshot_url); }} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-gray-600 hover:underline mt-1"
                  data-testid="tiktok-screenshot-link"
                >
                  <Camera className="h-3 w-3" />
                  Ekran Görüntüsü
                </a>
              )}
            </div>

            {/* YouTube */}
            <div className="space-y-2 p-4 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Youtube className="h-5 w-5 text-red-600" />
                <span className="font-medium">YouTube</span>
              </div>
              <div className="text-sm">
                <p>@{user.youtube_username || '-'}</p>
                <p className="text-gray-500">{user.youtube_followers?.toLocaleString() || 0} abone</p>
              </div>
              <VerificationBadge verified={user.youtube_username_verified} field="youtube_username" />
              {user.youtube_screenshot_url && (
                <a 
                  href="#" onClick={(e) => { e.preventDefault(); openPrivateFile(user.youtube_screenshot_url); }} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline mt-1"
                  data-testid="youtube-screenshot-link"
                >
                  <Camera className="h-3 w-3" />
                  Ekran Görüntüsü
                </a>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaigns */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Katıldığı Kampanyalar ({campaigns.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <p className="text-center text-gray-500 py-4">Henüz kampanyaya katılmamış</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kampanya</TableHead>
                  <TableHead>Katılımcı No</TableHead>
                  <TableHead>Katılım Tarihi</TableHead>
                  <TableHead>Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.campaigns?.title || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">#{c.participant_number || '-'}</Badge>
                    </TableCell>
                    <TableCell>{formatDate(c.joined_at)}</TableCell>
                    <TableCell>
                      <Badge variant={c.campaigns?.status === 'open' ? 'default' : 'secondary'}>
                        {c.campaigns?.status === 'open' ? 'Açık' : 'Kapalı'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Task Submissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Görev Gönderimleri ({submissions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {submissions.length === 0 ? (
            <p className="text-center text-gray-500 py-4">Henüz görev gönderimi yok</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kampanya</TableHead>
                  <TableHead>Görev</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>TL</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead>Link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.campaigns?.title || '-'}</TableCell>
                    <TableCell>{s.tasks?.title || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {s.tasks?.platform || '-'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={
                          s.status === 'approved' ? 'default' : 
                          s.status === 'rejected' ? 'destructive' : 
                          'secondary'
                        }
                        className={
                          s.status === 'approved' ? 'bg-green-100 text-green-800' : 
                          s.status === 'cancelled' ? 'bg-gray-100 text-gray-800' : ''
                        }
                      >
                        {s.status === 'approved' ? 'Onaylandı' : 
                         s.status === 'rejected' ? 'Reddedildi' : 
                         s.status === 'cancelled' ? 'İptal Edildi' : 'Beklemede'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {s.status === 'approved' ? (
                        <span className="font-medium text-green-600">{formatCurrency(s.tasks?.payment_amount)}</span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm">{formatDateTime(s.submitted_at)}</TableCell>
                    <TableCell>
                      {s.submitted_link && (
                        <a href={s.submitted_link} target="_blank" rel="noopener noreferrer"
                          className="text-brand-ink font-semibold underline-offset-2 hover:underline text-xs flex items-center gap-1">
                          Link
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Activity Logs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Islem Loglari
            </CardTitle>
            <Button variant="outline" size="sm" onClick={toggleLogs} data-testid="toggle-logs-btn">
              {logsVisible ? 'Gizle' : 'Goster'}
            </Button>
          </div>
        </CardHeader>
        {logsVisible && (
          <CardContent>
            {/* Filter */}
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select value={logFilter} onValueChange={handleLogFilterChange}>
                <SelectTrigger className="w-[200px]" data-testid="log-filter-select">
                  <SelectValue placeholder="Islem Turu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tumu</SelectItem>
                  <SelectItem value="profile_update">Profil Guncelleme</SelectItem>
                  <SelectItem value="social_verification_success">Sosyal Medya Dogrulama</SelectItem>
                  <SelectItem value="task_submission">Görev Gönderimi</SelectItem>
                  <SelectItem value="submission_cancelled">Gönderim İptali</SelectItem>
                  <SelectItem value="campaign_join">Kampanya Katilim</SelectItem>
                  <SelectItem value="login">Giris</SelectItem>
                  <SelectItem value="register">Kayit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {logsLoading ? (
              <p className="text-center text-gray-500 py-4">Yukleniyor...</p>
            ) : activityLogs.length === 0 ? (
              <p className="text-center text-gray-500 py-4">Islem logu bulunamadi</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead>Islem</TableHead>
                    <TableHead>Detay</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activityLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm whitespace-nowrap">{formatDateTime(log.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{getActionTypeLabel(log.action_type)}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 max-w-[300px] truncate">
                        {log.details ? JSON.stringify(log.details) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
